// Products module — main page.
// Airtable / Linear / Notion-database inspired table for Shopify products.
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Loader2, RefreshCw, Download, X, Tag as TagIcon,
  Package, AlertTriangle, ArrowUpDown, Save,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  listProducts, syncProducts, getSyncStatus, exportCsvUrl,
} from "@/lib/products";
import { listSuppliers } from "@/lib/inventory";
import ProductsTable from "@/pages/products/ProductsTable";
import ProductSidebar from "@/pages/products/ProductSidebar";
import BulkBar from "@/pages/products/BulkBar";

const FILTERS = [
  { id: "all", label: "Toate" },
  { id: "active", label: "Active" },
  { id: "draft", label: "Draft" },
  { id: "archived", label: "Arhivate" },
  { id: "out_of_stock", label: "Stoc 0", icon: AlertTriangle },
  { id: "low_stock", label: "Stoc redus" },
  { id: "on_sale", label: "La reducere", icon: TagIcon },
];

const SORTS = [
  { id: "updated_desc", label: "Actualizate recent" },
  { id: "title_asc", label: "Titlu A→Z" },
  { id: "title_desc", label: "Titlu Z→A" },
  { id: "inventory_asc", label: "Stoc ↑" },
  { id: "inventory_desc", label: "Stoc ↓" },
  { id: "price_desc", label: "Preț ↓" },
  { id: "price_asc", label: "Preț ↑" },
];

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("updated_desc");
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [activeProductId, setActiveProductId] = useState(searchParams.get("id"));
  const [sidebarOpen, setSidebarOpen] = useState(!!searchParams.get("id"));
  const [suppliers, setSuppliers] = useState([]);
  // Map of productId → {save: () => Promise} for all dirty rows.
  // Filled via onDirtyChange callback from each row.
  const [dirtyRows, setDirtyRows] = useState(new Map());
  const [savingAll, setSavingAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const scrollRef = useRef(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listProducts({ search, filter, sort, limit: 100 });
      setItems(r.items || []);
      setNextCursor(r.next_cursor);
    } catch (e) {
      toast.error("Eroare la încărcarea produselor");
    } finally {
      setLoading(false);
    }
  }, [search, filter, sort]);

  useEffect(() => {
    const t = setTimeout(reload, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [reload, search]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await listProducts({ search, filter, sort, cursor: nextCursor, limit: 100 });
      setItems((prev) => [...prev, ...(r.items || [])]);
      setNextCursor(r.next_cursor);
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, search, filter, sort]);

  // Infinite scroll on the table viewport
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (!nextCursor || loadingMore) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 400) loadMore();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [nextCursor, loadingMore, loadMore]);

  const reloadStatus = useCallback(async () => {
    try { setSyncStatus(await getSyncStatus()); } catch {}
  }, []);
  useEffect(() => { reloadStatus(); }, [reloadStatus]);

  const handleSync = async (full = false) => {
    setSyncing(true);
    const tid = toast.loading(full ? "Sincronizez TOATE produsele..." : "Sincronizez modificările recente...");
    try {
      const r = await syncProducts(full);
      if (r.ok) {
        toast.success(`Sincronizat ${r.synced} produse`, { id: tid });
        await reload();
        await reloadStatus();
      } else {
        toast.error(r.error || "Sync eșuat", { id: tid });
      }
    } catch (e) {
      toast.error("Sync eșuat", { id: tid });
    } finally {
      setSyncing(false);
    }
  };

  const openProduct = (id) => {
    setActiveProductId(id);
    setSidebarOpen(true);
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.set("id", id);
      return next;
    });
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setActiveProductId(null);
    setSearchParams((p) => {
      const next = new URLSearchParams(p);
      next.delete("id");
      return next;
    });
  };

  // Update a row in-place after Save (returns new product doc from API)
  const replaceItem = (productId, newDoc) => {
    setItems((prev) => prev.map((p) => (p.id === productId ? { ...p, ...newDoc } : p)));
  };

  const onSelectAll = (val) => {
    if (val) setSelected(new Set(items.map((i) => i.id)));
    else setSelected(new Set());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Suppliers fetch (used by inline picker)
  useEffect(() => {
    listSuppliers().then(setSuppliers).catch(() => {});
  }, []);

  // Row-level dirty registry (called from each ProductRow).
  const onDirtyChange = useCallback((productId, isDirty, saveFn) => {
    setDirtyRows((prev) => {
      const next = new Map(prev);
      if (isDirty) next.set(productId, saveFn);
      else next.delete(productId);
      return next;
    });
  }, []);

  const saveAllDirty = async () => {
    if (dirtyRows.size === 0 || savingAll) return;
    setSavingAll(true);
    const tid = toast.loading(`Salvez ${dirtyRows.size} modificări...`);
    let ok = 0;
    let failed = 0;
    for (const [, saveFn] of dirtyRows) {
      try { await saveFn?.(); ok++; }
      catch { failed++; }
    }
    setSavingAll(false);
    if (failed === 0) toast.success(`${ok} modificări salvate`, { id: tid });
    else toast.error(`${ok} salvate · ${failed} eșuate`, { id: tid });
  };

  const discardAll = () => {
    if (!window.confirm(`Renunți la ${dirtyRows.size} modificări nesalvate?`)) return;
    // Force the table to re-mount its rows by bumping the refresh key.
    setRefreshKey((k) => k + 1);
    setDirtyRows(new Map());
  };

  return (
    <div
      className="h-[calc(100vh-7rem)] -m-4 sm:-m-6 lg:-m-8 flex flex-col bg-background border-t border-border"
      data-testid="products-page"
    >
      {/* ===== HEADER ===== */}
      <div className="border-b border-border bg-background/60 backdrop-blur sticky top-0 z-20">
        <div className="px-6 py-4 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 mr-2">
            <Package className="h-5 w-5 text-primary" />
            <div>
              <div className="font-display font-semibold text-lg leading-tight">Produse</div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                <span data-testid="products-total">{syncStatus?.total ?? 0} produse</span>
                {syncStatus?.active != null && <span>· {syncStatus.active} active</span>}
                {syncStatus?.out_of_stock != null && <span>· {syncStatus.out_of_stock} fără stoc</span>}
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[280px] max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută SKU, titlu, vendor, barcode, tag..."
              className="pl-9 pr-8 h-9 bg-secondary/40 border-border"
              data-testid="products-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="products-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Sort */}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="products-sort">
              <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`products-sort-${s.id}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => handleSync(false)}
              disabled={syncing}
              data-testid="products-sync-btn"
            >
              {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Sync
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={() => handleSync(true)}
              disabled={syncing}
              data-testid="products-sync-full-btn"
              title="Sincronizează absolut toate produsele"
            >
              Sync complet
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              asChild
              data-testid="products-export-btn"
            >
              <a href={exportCsvUrl([])} download>
                <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
              </a>
            </Button>
          </div>
        </div>

        {/* Filter pills */}
        <div className="px-6 pb-3 flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`products-filter-${f.id}`}
              className={`text-[12px] px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                filter === f.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary/50 text-muted-foreground hover:bg-secondary"
              }`}
            >
              {f.icon ? <f.icon className="h-3 w-3" /> : null}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== TABLE ===== */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto scrollbar-thin"
        data-testid="products-table-scroll"
      >
        <ProductsTable
          key={refreshKey}
          items={items}
          loading={loading}
          loadingMore={loadingMore}
          selected={selected}
          onToggleSelect={toggleSelect}
          onSelectAll={onSelectAll}
          onOpen={openProduct}
          activeProductId={activeProductId}
          onReplaceItem={replaceItem}
          suppliers={suppliers}
          onDirtyChange={onDirtyChange}
        />
        {nextCursor && (
          <div className="py-4 text-center text-xs text-muted-foreground">
            {loadingMore ? (
              <Loader2 className="h-4 w-4 animate-spin inline" />
            ) : (
              <button onClick={loadMore} className="hover:text-foreground" data-testid="products-load-more">
                Încarcă mai multe
              </button>
            )}
          </div>
        )}
      </div>

      {/* ===== PENDING SAVE BAR ===== */}
      {dirtyRows.size > 0 && (
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-40 bg-amber-500/10 border border-amber-500/40 backdrop-blur-md rounded-2xl shadow-2xl px-4 py-2.5 flex items-center gap-3"
          data-testid="products-pending-bar"
        >
          <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
          <div className="text-sm font-medium" data-testid="products-pending-count">
            {dirtyRows.size} {dirtyRows.size === 1 ? "modificare nesalvată" : "modificări nesalvate"}
          </div>
          <div className="h-5 w-px bg-amber-500/30" />
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-muted-foreground hover:text-foreground"
            onClick={discardAll}
            disabled={savingAll}
            data-testid="products-discard-all"
          >
            <X className="h-3.5 w-3.5 mr-1.5" /> Renunță
          </Button>
          <Button
            size="sm"
            className="h-8 bg-amber-500 hover:bg-amber-600 text-white"
            onClick={saveAllDirty}
            disabled={savingAll}
            data-testid="products-save-all"
          >
            {savingAll ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save changes
          </Button>
        </div>
      )}

      {/* ===== BULK BAR ===== */}
      {selected.size > 0 && (
        <BulkBar
          selectedIds={Array.from(selected)}
          onClear={() => setSelected(new Set())}
          onDone={async () => {
            setSelected(new Set());
            await reload();
            await reloadStatus();
          }}
        />
      )}

      {/* ===== SIDEBAR ===== */}
      <ProductSidebar
        open={sidebarOpen}
        productId={activeProductId}
        onClose={closeSidebar}
      />
    </div>
  );
}
